import React, { useState, useEffect, useMemo } from "react";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { FaClipboardList, FaSave } from "react-icons/fa";

const MarkRollEntryPage = () => {
  const [form, setForm] = useState({
    exam_id: "",
    class_id: "",
    section_id: "",
    subject_id: "",
  });

  const [dropdowns, setDropdowns] = useState({
    exams: [],
    classes: [],
    sections: [],
  });

  const [existingSubjects, setExistingSubjects] = useState([]);
  const [subjectInfo, setSubjectInfo] = useState(null);
  const [markTypes, setMarkTypes] = useState([]);
  const [students, setStudents] = useState([]);
  const [marksData, setMarksData] = useState([]);
  const [studentStats, setStudentStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
  });

  const [filterMode, setFilterMode] = useState("pending"); // pending | completed | total
  const [loading, setLoading] = useState(false);

  // ---------- Toast ----------
  const showToast = (type, message) => {
    const options = {
      position: "top-right",
      autoClose: 2000,
      theme: "colored",
    };
    switch (type) {
      case "success":
        toast.success(message, options);
        break;
      case "error":
        toast.error(message, options);
        break;
      case "warning":
        toast.warning(message, options);
        break;
      default:
        toast.info(message, options);
    }
  };

  // ---------- Initial loads ----------
  useEffect(() => {
    loadExams();
    loadClasses();
  }, []);

  const loadExams = async () => {
    try {
      const res = await api.get("/exam-management/list");
      if (res.data?.status && Array.isArray(res.data.data)) {
        setDropdowns((p) => ({ ...p, exams: res.data.data }));
      }
    } catch {
      showToast("error", "Failed to load exams");
    }
  };

  const loadClasses = async () => {
    try {
      const res = await api.get("/get-classes");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setDropdowns((p) => ({ ...p, classes: res.data.data }));
      }
    } catch {
      showToast("error", "Failed to load classes");
    }
  };

  const loadSections = async (classId) => {
    try {
      const res = await api.get(`/get-sections/${classId}`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setDropdowns((p) => ({ ...p, sections: res.data.data }));
      } else {
        setDropdowns((p) => ({ ...p, sections: [] }));
      }
    } catch {
      setDropdowns((p) => ({ ...p, sections: [] }));
    }
  };

  // ---------- Existing subjects (summary) ----------
  const loadExistingSubjects = async (examId, classId, sectionId) => {
    if (!examId || !classId || !sectionId) {
      setExistingSubjects([]);
      return;
    }
    try {
      const res = await api.get(
        `/mark-roll/existing?exam_id=${examId}&class_id=${classId}&section_id=${sectionId}`
      );
      if (res.data?.status && Array.isArray(res.data.data)) {
        setExistingSubjects(res.data.data);
      } else {
        setExistingSubjects([]);
      }
    } catch {
      setExistingSubjects([]);
      showToast("error", "Error loading subjects");
    }
  };

  // ---------- Load students + mark types ----------
  const loadStudents = async (examId, classId, sectionId, subjectId) => {
    if (!examId || !classId || !subjectId) return;

    try {
      const res = await api.get(
        `/mark-roll/students?exam_id=${examId}&class_id=${classId}&section_id=${sectionId}&subject_id=${subjectId}`
      );

      if (res.data?.status && res.data.data) {
        const { subject_info, mark_types, students: stuList, student_stats } =
          res.data.data;

        setSubjectInfo(subject_info || null);
        setMarkTypes(mark_types || []);
        setStudents(stuList || []);
        setStudentStats(
          student_stats || { total: stuList?.length || 0, completed: 0, pending: 0 }
        );

        // marksData: always 1 row per student
        const prepared = (stuList || []).map((s) => {
          const items = (mark_types || []).map((mt) => {
            const mtId = mt.id;
            let value = "";

            if (s.marks && Array.isArray(s.marks.items)) {
              const found = s.marks.items.find(
                (it) => String(it.mark_type_id) === String(mtId)
              );
              if (found && found.marks_obtained !== null) {
                value = String(found.marks_obtained);
              }
            }

            return {
              mark_type_id: mtId,
              marks_obtained: value,
            };
          });

          return {
            student_id: s.student_id,
            remarks: s.marks?.remarks || "",
            items,
          };
        });

        setMarksData(prepared);
        setFilterMode("pending"); // default tab
      } else {
        setSubjectInfo(null);
        setMarkTypes([]);
        setStudents([]);
        setMarksData([]);
        setStudentStats({ total: 0, completed: 0, pending: 0 });
      }
    } catch (e) {
      console.error(e);
      showToast("error", "Failed to load students or marks structure");
      setSubjectInfo(null);
      setMarkTypes([]);
      setStudents([]);
      setMarksData([]);
      setStudentStats({ total: 0, completed: 0, pending: 0 });
    }
  };

  // ---------- Dropdown change ----------
  const handleChange = async (key, value) => {
    const updated = { ...form, [key]: value };

    if (key === "class_id") {
      updated.section_id = "";
      updated.subject_id = "";
      setDropdowns((p) => ({ ...p, sections: [] }));
      setExistingSubjects([]);
      setStudents([]);
      setMarksData([]);
      setSubjectInfo(null);
      setMarkTypes([]);
      setStudentStats({ total: 0, completed: 0, pending: 0 });

      if (value) loadSections(value);
    }

    if (key === "section_id") {
      updated.subject_id = "";
      setStudents([]);
      setMarksData([]);
      setSubjectInfo(null);
      setMarkTypes([]);
      setStudentStats({ total: 0, completed: 0, pending: 0 });

      if (updated.exam_id && updated.class_id && value) {
        await loadExistingSubjects(updated.exam_id, updated.class_id, value);
      } else {
        setExistingSubjects([]);
      }
    }

    if (key === "exam_id") {
      updated.subject_id = "";
      setStudents([]);
      setMarksData([]);
      setSubjectInfo(null);
      setMarkTypes([]);
      setStudentStats({ total: 0, completed: 0, pending: 0 });

      if (updated.class_id && updated.section_id) {
        await loadExistingSubjects(value, updated.class_id, updated.section_id);
      } else {
        setExistingSubjects([]);
      }
    }

    if (key === "subject_id") {
      // allow re-open even if some marks exist; rows will be readonly for completed students
      if (updated.exam_id && updated.class_id && updated.section_id && value) {
        await loadStudents(
          updated.exam_id,
          updated.class_id,
          updated.section_id,
          value
        );
      } else {
        setStudents([]);
        setMarksData([]);
        setSubjectInfo(null);
        setMarkTypes([]);
        setStudentStats({ total: 0, completed: 0, pending: 0 });
      }
    }

    if (!value && key !== "exam_id") {
      setStudents([]);
      setMarksData([]);
      setSubjectInfo(null);
      setMarkTypes([]);
      setStudentStats({ total: 0, completed: 0, pending: 0 });
    }

    setForm(updated);
  };

  // ---------- Marks change ----------
  const handleMarkChange = (studentId, markTypeId, rawValue, maxAllowed) => {
    let value = rawValue;

    if (value === "") {
      setMarksData((prev) =>
        prev.map((row) =>
          row.student_id === studentId
            ? {
                ...row,
                items: row.items.map((it) =>
                  String(it.mark_type_id) === String(markTypeId)
                    ? { ...it, marks_obtained: "" }
                    : it
                ),
              }
            : row
        )
      );
      return;
    }

    const num = Number(value);
    if (Number.isNaN(num)) return;
    if (num < 0) return;
    if (num > maxAllowed) {
      showToast("warning", `Max marks for this type is ${maxAllowed}`);
      return;
    }

    setMarksData((prev) =>
      prev.map((row) =>
        row.student_id === studentId
          ? {
              ...row,
              items: row.items.map((it) =>
                String(it.mark_type_id) === String(markTypeId)
                  ? { ...it, marks_obtained: value }
                  : it
              ),
            }
          : row
      )
    );
  };

  const handleRemarksChange = (studentId, value) => {
    setMarksData((prev) =>
      prev.map((row) =>
        row.student_id === studentId ? { ...row, remarks: value } : row
      )
    );
  };

  // ---------- Submit ----------
  const handleSubmit = async () => {
    if (loading) return;

    if (!form.exam_id || !form.class_id || !form.subject_id) {
      return showToast(
        "warning",
        "Please select Exam, Class, Section & Subject!"
      );
    }
    if (!students.length) {
      return showToast("warning", "No students found to save marks!");
    }

    const payload = {
      exam_id: form.exam_id,
      class_id: form.class_id,
      section_id: form.section_id || 0,
      subject_id: form.subject_id,
      marks: marksData.map((row) => ({
        student_id: row.student_id,
        remarks: row.remarks,
        items: row.items.map((it) => ({
          mark_type_id: it.mark_type_id,
          marks_obtained:
            it.marks_obtained === "" ? "" : parseFloat(it.marks_obtained),
        })),
      })),
    };

    setLoading(true);
    try {
      const res = await api.post("/mark-roll/entry", payload);

      if (res.data?.status) {
        showToast("success", res.data.message || "Marks saved successfully!");

        setForm({
          exam_id: form.exam_id,
          class_id: "",
          section_id: "",
          subject_id: "",
        });
        setDropdowns((p) => ({ ...p, sections: [] }));
        setExistingSubjects([]);
        setStudents([]);
        setMarksData([]);
        setSubjectInfo(null);
        setMarkTypes([]);
        setStudentStats({ total: 0, completed: 0, pending: 0 });
        setFilterMode("pending");
      } else {
        showToast("error", res.data?.message || "Failed to save marks!");
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message || err?.message || "Server error!";
      showToast("error", msg);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Filtered students (Pending / Completed / Total) ----------
  const filteredStudents = useMemo(() => {
    if (!students.length) return [];

    if (filterMode === "pending") return students.filter((s) => !s.marks);
    if (filterMode === "completed") return students.filter((s) => !!s.marks);
    return students;
  }, [students, filterMode]);

  // fallback stats if API doesn’t send student_stats
  const derivedCompleted = useMemo(
    () => students.filter((s) => !!s.marks).length,
    [students]
  );
  const derivedTotal = students.length;
  const derivedPending = derivedTotal - derivedCompleted;

  const stats = {
    total: studentStats.total || derivedTotal,
    completed: studentStats.completed || derivedCompleted,
    pending: studentStats.pending || derivedPending,
  };

  // ---------- UI ----------
  return (
    <>
      <ToastContainer />

      <style>{`
        body {
          background: #f3f4f6;
          font-family: 'Poppins', sans-serif;
        }

        .mr-header {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #ffffff;
          padding: 52px 20px 80px;
          text-align: center;
          border-bottom-left-radius: 56% 12%;
          border-bottom-right-radius: 56% 12%;
          box-shadow: 0 12px 30px rgba(37, 99, 235, 0.3);
        }

        .mr-header h1 {
          font-weight: 800;
          font-size: 28px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }

        .mr-header p {
          margin-top: 4px;
          font-size: 13px;
          opacity: 0.9;
        }

        .mr-card {
          max-width: 1500px;
          margin: -64px auto 40px;
          background: #ffffff;
          border-radius: 18px;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
          padding: 22px 26px 28px;
        }

        .mr-filters {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
          gap: 14px;
          margin-bottom: 12px;
          z-index: 99;
        }

        .mr-top-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 8px;
          margin-bottom: 10px;
        }

        .mr-subject-chip {
          background: #eff6ff;
          border-radius: 999px;
          padding: 6px 14px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #1e3a8a;
        }

        .mr-subject-chip span.title {
          font-weight: 600;
        }

        .mr-subject-chip span.meta {
          opacity: 0.9;
        }

        .mr-filter-tabs {
          display: inline-flex;
          gap: 6px;
          background: #f3f4f6;
          padding: 3px;
          border-radius: 999px;
        }

        .mr-filter-tab-btn {
          border: none;
          padding: 5px 12px;
          border-radius: 999px;
          font-size: 12px;
          cursor: pointer;
          background: transparent;
          color: #374151;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .mr-filter-tab-btn span.count {
          font-weight: 600;
        }

        .mr-filter-tab-btn.active {
          background: #ffffff;
          color: #1d4ed8;
          box-shadow: 0 1px 4px rgba(15, 23, 42, 0.15);
        }

        .mr-subject-status {
          margin-top: 6px;
          padding: 10px 12px 8px;
          background: #f9fafb;
          border-radius: 10px;
          border: 1px dashed #e5e7eb;
          font-size: 12px;
        }

        .mr-subject-status h4 {
          color: #1d4ed8;
          margin-bottom: 6px;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .mr-subject-item {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
          border-bottom: 1px dashed #e5e7eb;
          font-size: 12px;
        }

        .mr-subject-item:last-child {
          border-bottom: none;
        }

        .mr-badge {
          padding: 2px 9px;
          border-radius: 999px;
          font-weight: 600;
          font-size: 11px;
        }

        .mr-badge-completed {
          background: #dcfce7;
          color: #166534;
        }

        .mr-marks-wrapper {
          margin-top: 14px;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .mr-marks-table-wrapper {
          max-height: 430px;
          overflow-y: auto;
        }

        .mr-marks-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 650px;
        }

        .mr-marks-table thead th {
          position: sticky;
          top: 0;
          z-index: 0;
          background: #2563eb;
          color: #ffffff;
          font-size: 12px;
          padding: 8px 8px 6px;
          border-bottom: 1px solid #1d4ed8;
        }

        .mr-marktype-header {
          font-weight: 600;
        }

        .mr-marktype-sub {
          font-size: 10px;
          opacity: 0.9;
        }

        .mr-marks-table tbody td {
          padding: 7px 8px;
          font-size: 13px;
          border-bottom: 1px solid #e5e7eb;
          text-align: center;
        }

        .mr-marks-table tbody tr:last-child td {
          border-bottom: none;
        }

        .mr-marks-table tbody tr:hover {
          background: #f5f7ff;
        }

        .mr-row-completed {
          background: #f3f4f6;
        }

        .mr-td-name {
          text-align: left;
          font-weight: 500;
          color: #111827;
        }

        .mr-td-roll {
          font-weight: 600;
          color: #111827;
        }

        .mr-input-mark {
          width: 50%;
          max-width: 100%;
          padding: 5px 6px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          text-align: center;
          font-size: 12px;
          background: #f8fafc;
          transition: all 0.15s;
        }

        .mr-input-mark:focus {
          background: #ffffff;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
          outline: none;
        }

        .mr-input-mark[disabled] {
          background: #e5e7eb;
          cursor: not-allowed;
          border-color: #d1d5db;
          box-shadow: none;
        }

        .mr-input-remarks {
          width: 50%;
          padding: 5px 7px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          font-size: 12px;
          text-align: left;
          background: #f8fafc;
        }

        .mr-input-remarks:focus {
          background: #ffffff;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
          outline: none;
        }

        .mr-input-remarks[disabled] {
          background: #e5e7eb;
          cursor: not-allowed;
          border-color: #d1d5db;
          box-shadow: none;
        }

        .mr-save-btn {
          background: linear-gradient(90deg, #2563eb, #3b82f6);
          color: #ffffff;
          border: none;
          padding: 11px 24px;
          border-radius: 999px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin: 22px auto 0;
          cursor: pointer;
          transition: 0.25s;
        }

        .mr-save-btn:hover {
          transform: translateY(-1px) scale(1.02);
          box-shadow: 0 10px 24px rgba(37, 99, 235, 0.35);
        }

        .mr-save-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          box-shadow: none;
          transform: none;
        }

        .mr-empty-text {
          text-align: center;
          color: #6b7280;
          font-size: 13px;
          padding: 18px 0 12px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header */}
      <div className="mr-header">
        <h1>
          <FaClipboardList /> Mark Roll Entry
        </h1>
        <p>Enter subject-wise marks with dynamic mark types in a clean grid</p>
      </div>

      {/* Main Card */}
      <div className="mr-card">
        {/* Filters */}
        <div className="mr-filters">
          {/* Exam */}
          <Select
            options={dropdowns.exams.map((e) => ({
              label: e.exam_name,
              value: e.id,
            }))}
            value={
              dropdowns.exams
                .map((e) => ({ label: e.exam_name, value: e.id }))
                .find((x) => String(x.value) === String(form.exam_id)) || null
            }
            placeholder="Select Exam"
            onChange={(opt) => handleChange("exam_id", opt ? opt.value : "")}
            isClearable
          />

          {/* Class */}
          <Select
            options={dropdowns.classes.map((c) => ({
              label: c.class_name,
              value: c.id,
            }))}
            value={
              dropdowns.classes
                .map((c) => ({ label: c.class_name, value: c.id }))
                .find((x) => String(x.value) === String(form.class_id)) || null
            }
            placeholder="Select Class"
            onChange={(opt) => handleChange("class_id", opt ? opt.value : "")}
            isClearable
          />

          {/* Section */}
          <Select
            options={dropdowns.sections.map((s) => ({
              label: s.section_name,
              value: s.id,
            }))}
            value={
              dropdowns.sections
                .map((s) => ({ label: s.section_name, value: s.id }))
                .find((x) => String(x.value) === String(form.section_id)) ||
              null
            }
            placeholder="Select Section"
            onChange={(opt) =>
              handleChange("section_id", opt ? opt.value : "")
            }
            isClearable
            isDisabled={!form.class_id}
          />

          {/* Subject */}
          <Select
            options={existingSubjects.map((s) => ({
              label: s.subject_name,
              value: s.subject_id,
            }))}
            value={
              existingSubjects
                .map((s) => ({
                  label: s.subject_name,
                  value: s.subject_id,
                }))

                .find((x) => String(x.value) === String(form.subject_id)) ||
              null
            }
            placeholder="Select Subject"
            onChange={(opt) =>
              handleChange("subject_id", opt ? opt.value : "")
            }
            isClearable
            isDisabled={!form.section_id || !existingSubjects.length}
          />
        </div>

        {/* Subject info + Tabs */}
        {subjectInfo && (
          <div className="mr-top-row">
            <div className="mr-subject-chip">
              <span className="title">{subjectInfo.subject_name}</span>
              <span className="meta">
                Max: {subjectInfo.total_max_marks} | Pass:{" "}
                {subjectInfo.total_pass_marks} | Exam #{subjectInfo.exam_subject_id}
              </span>
            </div>

            {students.length > 0 && (
              <div className="mr-filter-tabs">
                <button
                  className={`mr-filter-tab-btn ${
                    filterMode === "pending" ? "active" : ""
                  }`}
                  onClick={() => setFilterMode("pending")}
                >
                  Pending <span className="count">({stats.pending})</span>
                </button>
                <button
                  className={`mr-filter-tab-btn ${
                    filterMode === "completed" ? "active" : ""
                  }`}
                  onClick={() => setFilterMode("completed")}
                >
                  Completed <span className="count">({stats.completed})</span>
                </button>
                <button
                  className={`mr-filter-tab-btn ${
                    filterMode === "total" ? "active" : ""
                  }`}
                  onClick={() => setFilterMode("total")}
                >
                  Total <span className="count">({stats.total})</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Completed subject list (optional info) */}
        {existingSubjects.some((s) => s.status === "Completed") && (
          <div className="mr-subject-status">
            <h4>
              ✅ Completed Subjects{" "}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: "#6b7280",
                }}
              >
                (subject-level summary)
              </span>
            </h4>
            {existingSubjects
              .filter((s) => s.status === "Completed")
              .map((s, i) => (
                <div key={i} className="mr-subject-item">
                  <span>
                    {s.subject_name} — Max: {s.max_marks} / Pass: {s.pass_marks}
                  </span>
                  <span className="mr-badge mr-badge-completed">
                    Completed
                  </span>
                </div>
              ))}
          </div>
        )}

        {/* Marks table */}
        <div className="mr-marks-wrapper">
          {filteredStudents.length > 0 && markTypes.length > 0 ? (
            <div className="mr-marks-table-wrapper">
              <table className="mr-marks-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Roll</th>
                    <th style={{ textAlign: "left", minWidth: 170 }}>
                      Student Name
                    </th>
                    {markTypes.map((mt) => {
                      const max = parseFloat(mt.max_marks || 0);
                      const pass = parseFloat(mt.pass_marks || 0);
                      const hasPass = String(mt.has_pass) === "1" && pass > 0;

                      return (
                        <th key={mt.id}>
                          <div className="mr-marktype-header">{mt.title}</div>
                          <div className="mr-marktype-sub">
                            Max: {max}
                            {hasPass && ` | Pass: ${pass}`}
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ minWidth: 130 }}>Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredStudents.map((s) => {
                    const row =
                      marksData.find((r) => r.student_id === s.student_id) ||
                      {};
               const isCompleted = false;

                    return (
                      <tr
                        key={s.student_id}
                        className={isCompleted ? "mr-row-completed" : ""}
                      >
                        <td className="mr-td-roll">{s.roll_no || ""}</td>
                        <td className="mr-td-name">{s.name}</td>

                        {markTypes.map((mt) => {
                          const max = parseFloat(mt.max_marks || 0);
                          const item =
                            row.items?.find(
                              (it) =>
                                String(it.mark_type_id) === String(mt.id)
                            ) || {};

                          return (
                            <td key={mt.id}>
                              <input
                                type="number"
                                className="mr-input-mark"
                                value={item.marks_obtained ?? ""}
                                disabled={false}
                                onChange={(e) =>
                                  handleMarkChange(
                                    s.student_id,
                                    mt.id,
                                    e.target.value,
                                    max
                                  )
                                }
                                placeholder="Marks"
                              />
                            </td>
                          );
                        })}

                        <td>
                          <input
                            type="text"
                            className="mr-input-remarks"
                            value={row.remarks || ""}
                            disabled={false}
                            onChange={(e) =>
                              handleRemarksChange(
                                s.student_id,
                                e.target.value
                              )
                            }
                            placeholder="Good / Excellent"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mr-empty-text">
              {form.exam_id && form.class_id && form.section_id
                ? "Select subject to load students and mark types."
                : "Please select Exam, Class & Section first."}
            </p>
          )}
        </div>

        {/* Save button */}
        <button
          className="mr-save-btn"
          onClick={handleSubmit}
          disabled={loading || !students.length || !markTypes.length}
        >
          {loading ? (
            <>
              <span
                style={{
                  width: "18px",
                  height: "18px",
                  border: "3px solid #fff",
                  borderTop: "3px solid transparent",
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: 4,
                  animation: "spin 0.8s linear infinite",
                }}
              ></span>
              Saving...
            </>
          ) : (
            <>
              <FaSave /> Save Marks
            </>
          )}
        </button>
      </div>
    </>
  );
};

export default MarkRollEntryPage;
